import { useState, useEffect, useRef, useMemo } from 'react';
import type { ExperienceElement, ElementStyle, ElementKeyframe } from '../types';
import { calculateAnimatedStyles } from '../utils';
import { computeKeyframeStyle, computeKeyframeText, findKeyframeAtTime } from '../../../utils/keyframeUtils';

/**
 * CSS to iOS points conversion factor.
 * Empirically calibrated: iOS/React Native renders text at ~85% the width of CSS.
 * This accounts for differences in font rendering between web browsers and iOS Core Text.
 */
const CSS_TO_IOS_FONT_SCALE = 0.85;

interface CanvasElementProps {
  element: ExperienceElement;
  selected: boolean;
  onSelect: () => void;
  onMove: (dx: number, dy: number) => void;
  onDragEnd?: () => void;
  onResize?: (width: number, height: number) => void;
  onScale?: (width: number, height: number, fontSize: number) => void;
  onUpdateText?: (text: string) => void;
  onUpdateTextKeyframe?: (text: string) => void;
  playbackTime?: number;
  previewTrackId?: number;
  selectedKeyframeTime?: number | null;
  previewVariables?: Record<string, string>;
  selectedStateId?: string | null;
}

export function CanvasElement({ element, selected, onSelect, onMove, onDragEnd, onResize, onScale, onUpdateText, onUpdateTextKeyframe, playbackTime = 0, previewTrackId = 1, selectedKeyframeTime, previewVariables = {}, selectedStateId }: CanvasElementProps) {
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [resizing, setResizing] = useState<string | null>(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, fontSize: 16 });
  const [shiftHeld, setShiftHeld] = useState(false);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  const hasAnimations = element.animations && element.animations.length > 0;

  // Get active state based on editing context
  // Priority: variable state (from tabs) only - keyframes now use per-element keyframes
  const activeState = useMemo(() => {
    if (!element.states || element.states.length === 0) return null;

    // If a variable state is selected (from tabs), use that
    if (selectedStateId) {
      return element.states.find(s => s.id === selectedStateId) || null;
    }

    return null;
  }, [element.states, selectedStateId]);

  // Get keyframes for current track from this element
  const elementKeyframes = useMemo((): ElementKeyframe[] => {
    return element.keyframes?.[previewTrackId] || [];
  }, [element.keyframes, previewTrackId]);

  // Compute keyframe style at current playback time
  const keyframeStyle = useMemo((): Partial<ElementStyle> => {
    if (selectedKeyframeTime !== null && selectedKeyframeTime !== undefined) {
      // When editing a keyframe, show accumulated style at that time
      const kf = findKeyframeAtTime(elementKeyframes, selectedKeyframeTime);
      if (kf) {
        return computeKeyframeStyle(elementKeyframes, selectedKeyframeTime, element.style || {});
      }
    }
    // Normal playback - interpolate
    return computeKeyframeStyle(elementKeyframes, playbackTime || 0, element.style || {});
  }, [elementKeyframes, playbackTime, selectedKeyframeTime, element.style]);

  // Compute text at current time (for text elements)
  const keyframeText = useMemo((): string | undefined => {
    if (element.type !== 'text') return undefined;
    const baseText = element.text;
    if (selectedKeyframeTime !== null && selectedKeyframeTime !== undefined) {
      return computeKeyframeText(elementKeyframes, selectedKeyframeTime, baseText);
    }
    return computeKeyframeText(elementKeyframes, playbackTime || 0, baseText);
  }, [element, elementKeyframes, playbackTime, selectedKeyframeTime]);

  // Check if we're editing a keyframe
  const isEditingKeyframe = (selectedKeyframeTime !== null && selectedKeyframeTime !== undefined) &&
    findKeyframeAtTime(elementKeyframes, selectedKeyframeTime) !== undefined;

  // Track Shift key state for proportional scaling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftHeld(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftHeld(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Focus textarea when entering edit mode and auto-size it
  useEffect(() => {
    if (isEditing && textInputRef.current) {
      textInputRef.current.focus();
      textInputRef.current.select();
      // Set initial height based on content
      textInputRef.current.style.height = 'auto';
      textInputRef.current.style.height = textInputRef.current.scrollHeight + 'px';
    }
  }, [isEditing]);

  // Exit edit mode when deselected
  useEffect(() => {
    if (!selected) {
      setIsEditing(false);
    }
  }, [selected]);

  // Calculate animated styles based on playback time
  const animatedStyles = calculateAnimatedStyles(element.animations, playbackTime);

  // Merge base style with animations, keyframe styles, and state overrides
  // Order: base -> animations -> keyframes -> states
  const effectiveStyle = useMemo(() => {
    const base = element.style || {};
    const animated = animatedStyles;
    const fromKeyframes = keyframeStyle;
    const fromState = activeState?.style || {};

    return {
      ...base,
      ...animated,
      ...fromKeyframes,
      ...fromState,
    };
  }, [element.style, animatedStyles, keyframeStyle, activeState]);

  // Build transform string from animated values (using effectiveStyle for keyframe overrides)
  const transforms: string[] = [];
  const scale = animatedStyles.scale ?? effectiveStyle?.scale;
  if (scale !== undefined && scale !== 1) transforms.push(`scale(${scale})`);
  const rotation = animatedStyles.rotation ?? effectiveStyle?.rotation;
  if (rotation !== undefined && rotation !== 0) transforms.push(`rotate(${rotation}deg)`);
  const translateX = animatedStyles.translateX ?? effectiveStyle?.translateX;
  if (translateX !== undefined && translateX !== 0) transforms.push(`translateX(${translateX}px)`);
  const translateY = animatedStyles.translateY ?? effectiveStyle?.translateY;
  if (translateY !== undefined && translateY !== 0) transforms.push(`translateY(${translateY}px)`);

  // Get animated opacity (with min visibility for editor)
  const animatedOpacity = animatedStyles.opacity ?? effectiveStyle?.opacity ?? 1;
  // Check visibility (default to true if not specified)
  const isVisible = effectiveStyle?.visible !== false;

  const isTextElement = element.type === 'text';

  const style: React.CSSProperties = {
    position: 'absolute',
    left: effectiveStyle?.x ?? 0,
    top: effectiveStyle?.y ?? 0,
    // Text elements: fixed width (for wrapping), auto height (grows with content)
    width: isTextElement ? (effectiveStyle?.width ?? 200) : effectiveStyle?.width,
    height: isTextElement ? 'auto' : effectiveStyle?.height,
    minWidth: isTextElement ? 20 : undefined,
    minHeight: isTextElement ? 20 : undefined,
    // Prevent text content from expanding the container
    overflow: isTextElement ? 'hidden' : undefined,
    opacity: isVisible ? Math.max(0.2, animatedOpacity) : 0,
    pointerEvents: isVisible ? 'auto' : 'none',
    transform: transforms.length > 0 ? transforms.join(' ') : 'none',
    backgroundColor: effectiveStyle?.backgroundColor,
    borderRadius: effectiveStyle?.borderRadius,
    color: effectiveStyle?.color,
    // Scale font size to simulate iOS rendering (72 DPI vs web's 96 DPI)
    fontSize: effectiveStyle?.fontSize ? effectiveStyle.fontSize * CSS_TO_IOS_FONT_SCALE : undefined,
    fontFamily: effectiveStyle?.fontFamily,
    fontWeight: effectiveStyle?.fontWeight,
    textAlign: effectiveStyle?.textAlign,
    // For text elements, always apply lineHeight (default 1.2 to match CSS normal)
    // Only apply custom lineHeight if >= 1.0 (RN clips text when lineHeight < fontSize)
    lineHeight: isTextElement
      ? ((effectiveStyle?.lineHeight && effectiveStyle.lineHeight >= 1.0) ? effectiveStyle.lineHeight : 1.2)
      : undefined,
    padding: effectiveStyle?.padding,
    cursor: isEditing ? 'text' : 'move',
    outline: selected
      ? (isEditingKeyframe ? '2px solid #a855f7' : '2px solid #3b82f6')
      : (hasAnimations ? '1px dashed #a855f7' : 'none'),
    outlineOffset: '2px',
    userSelect: isEditing ? 'text' : 'none',
  };

  // Handle mouse down to start drag (only if not editing)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isEditing) return;
    e.stopPropagation();
    e.preventDefault(); // Prevent text selection while dragging
    onSelect();
    setDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  // Handle double-click to enter edit mode for text elements
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (element.type === 'text') {
      setIsEditing(true);
    }
  };

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent, handle: string) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing(handle);
    const rect = elementRef.current?.getBoundingClientRect();
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: element.style?.width || rect?.width || 100,
      height: element.style?.height || rect?.height || 100,
      fontSize: element.style?.fontSize || 16,
    });
  };

  // Set up window-level mouse event listeners for dragging
  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      onMove(dx, dy);
      setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      setDragging(false);
      onDragEnd?.();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, dragStart, onMove, onDragEnd]);

  // Set up window-level mouse event listeners for resizing
  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - resizeStart.x;
      const dy = e.clientY - resizeStart.y;

      // Proportional scale mode when Shift is held
      if (e.shiftKey && onScale) {
        // Calculate scale based on diagonal movement from corner handles
        const diagonal = resizing.length === 2; // Corner handle (se, sw, ne, nw)
        let delta = 0;
        if (diagonal) {
          // For corners, use diagonal distance
          if (resizing === 'nw') delta = -(dx + dy) / 2;
          else if (resizing === 'se') delta = (dx + dy) / 2;
          else if (resizing === 'ne') delta = (dx - dy) / 2;
          else if (resizing === 'sw') delta = (-dx + dy) / 2;
        } else {
          // For edges, use the relevant axis
          delta = resizing === 'e' || resizing === 'w' ? dx : dy;
          if (resizing === 'w' || resizing === 'n') delta = -delta;
        }

        // Scale factor: 1 + delta / original dimension
        const baseDimension = Math.max(resizeStart.width, resizeStart.height);
        const scale = Math.max(0.1, 1 + delta / baseDimension);

        // Calculate new absolute values from original dimensions
        const newWidth = Math.max(20, resizeStart.width * scale);
        const newHeight = Math.max(20, resizeStart.height * scale);
        const newFontSize = Math.max(8, resizeStart.fontSize * scale);

        onScale(newWidth, newHeight, newFontSize);
      } else {
        // Normal resize mode
        let newWidth = resizeStart.width;
        let newHeight = resizeStart.height;

        // Adjust based on which handle is being dragged
        if (resizing.includes('e')) newWidth = Math.max(20, resizeStart.width + dx);
        if (resizing.includes('w')) newWidth = Math.max(20, resizeStart.width - dx);
        if (resizing.includes('s')) newHeight = Math.max(20, resizeStart.height + dy);
        if (resizing.includes('n')) newHeight = Math.max(20, resizeStart.height - dy);

        onResize?.(newWidth, newHeight);
      }
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, resizeStart, onResize, onScale]);

  // Render content based on element type
  const renderContent = () => {
    switch (element.type) {
      case 'text':
        if (isEditing) {
          // When editing with a keyframe selected, show/edit keyframe text
          const isKeyframeEdit = isEditingKeyframe;
          // Determine edit value: keyframe text > state text > base text
          const isStateEdit = selectedStateId && !isKeyframeEdit;
          let editValue = element.text;
          if (isKeyframeEdit && keyframeText !== undefined) {
            editValue = keyframeText;
          } else if (isStateEdit && activeState?.text !== undefined) {
            editValue = activeState.text;
          }
          return (
            <textarea
              ref={textInputRef}
              className="canvas-text-editor"
              value={editValue}
              onChange={(e) => {
                if (isKeyframeEdit) {
                  onUpdateTextKeyframe?.(e.target.value);
                } else {
                  onUpdateText?.(e.target.value);
                }
                // Auto-resize textarea
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              onBlur={() => setIsEditing(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsEditing(false);
                }
              }}
              style={{
                color: 'inherit',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                textAlign: 'inherit',
                lineHeight: 'inherit',
                backgroundColor: isKeyframeEdit ? 'rgba(168, 85, 247, 0.1)' : isStateEdit ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                width: '100%',
                height: 'auto',
                minHeight: '1em',
              }}
            />
          );
        }
        // Determine display text: state override > keyframe text > base text
        const displayText = activeState?.text !== undefined
          ? activeState.text
          : keyframeText ?? element.text;
        // Marquee mode: scrolling text
        if (effectiveStyle?.marquee) {
          return (
            <div className="marquee-container">
              <span className="marquee-content" style={{ lineHeight: 'inherit' }}>{displayText}</span>
            </div>
          );
        }
        return <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 'inherit', display: 'block', width: '100%' }}>{displayText}</span>;
      case 'image':
        return (
          <img
            src={element.src}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
          />
        );
      case 'video':
        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: '#333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#888',
              fontSize: 12,
            }}
          >
            Video: {element.src}
          </div>
        );
      case 'view':
        return null; // Empty container
      case 'button':
        return (
          <button
            style={{
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              pointerEvents: 'none',
              background: 'inherit',
              backgroundColor: 'inherit',
              borderRadius: 'inherit',
              color: 'inherit',
              border: 'none',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              fontFamily: 'inherit',
            }}
          >
            {element.text}
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={elementRef}
      className={`canvas-element ${selected ? 'selected' : ''} ${dragging ? 'dragging' : ''}`}
      style={style}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Inner wrapper to clip content to border radius without hiding resize handles */}
      <div style={{
        width: '100%',
        height: isTextElement ? 'auto' : '100%',
        borderRadius: 'inherit',
        overflow: effectiveStyle?.borderRadius ? 'hidden' : undefined,
      }}>
        {renderContent()}
      </div>
      {/* Resize handles - show when selected, dashed when Shift held for scale mode */}
      {selected && !isEditing && (
        <>
          <div className={`resize-handle resize-handle-se ${shiftHeld ? 'scale-mode' : ''}`} onMouseDown={(e) => handleResizeStart(e, 'se')} />
          <div className={`resize-handle resize-handle-sw ${shiftHeld ? 'scale-mode' : ''}`} onMouseDown={(e) => handleResizeStart(e, 'sw')} />
          <div className={`resize-handle resize-handle-ne ${shiftHeld ? 'scale-mode' : ''}`} onMouseDown={(e) => handleResizeStart(e, 'ne')} />
          <div className={`resize-handle resize-handle-nw ${shiftHeld ? 'scale-mode' : ''}`} onMouseDown={(e) => handleResizeStart(e, 'nw')} />
          <div className={`resize-handle resize-handle-e ${shiftHeld ? 'scale-mode' : ''}`} onMouseDown={(e) => handleResizeStart(e, 'e')} />
          <div className={`resize-handle resize-handle-w ${shiftHeld ? 'scale-mode' : ''}`} onMouseDown={(e) => handleResizeStart(e, 'w')} />
          <div className={`resize-handle resize-handle-s ${shiftHeld ? 'scale-mode' : ''}`} onMouseDown={(e) => handleResizeStart(e, 's')} />
          <div className={`resize-handle resize-handle-n ${shiftHeld ? 'scale-mode' : ''}`} onMouseDown={(e) => handleResizeStart(e, 'n')} />
        </>
      )}
    </div>
  );
}
